import React, {useEffect, useRef, useState} from 'react';
import {Line} from "react-chartjs-2";
import "./LineChart.scss";

const LineChart = ({ data, labels, className, lineColor }) => {
    const chartRef = useRef(null);
    const containerRef = useRef(null);
    const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

    useEffect(() => {
        const updateDimensions = () => {
            if (containerRef.current) {
                setDimensions({
                    width: containerRef.current.offsetWidth,
                    height: containerRef.current.offsetHeight
                });
            }
        };

        window.addEventListener('resize', updateDimensions);
        updateDimensions();

        return () => window.removeEventListener('resize', updateDimensions);
    }, []);

    const maxPixelsPerLabel = 75;
    const maxLabelsToShow = Math.floor(dimensions.width / maxPixelsPerLabel);
    const skipInterval = Math.ceil(labels.length / maxLabelsToShow);

    const chartData = {
        labels: labels,
        datasets: [
            {
                data: data,
                borderColor: lineColor,
                borderWidth: 2.5,
                backgroundColor: (context) => {
                    const ctx = context.chart.ctx;
                    const gradient = ctx.createLinearGradient(0, 0, 0, context.chart.height || 400);
                    if (lineColor && lineColor.includes('rgb')) {
                        const colorStart = lineColor.replace('rgb', 'rgba').replace(')', ', 0.3)');
                        const colorEnd = lineColor.replace('rgb', 'rgba').replace(')', ', 0.0)');
                        gradient.addColorStop(0, colorStart);
                        gradient.addColorStop(1, colorEnd);
                    } else {
                        gradient.addColorStop(0, 'rgba(0, 0, 0, 0.05)');
                        gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
                    }
                    return gradient;
                },
                fill: true,
                spanGaps: true,
                tension: 0.15,
                pointRadius: 0,
                pointHoverRadius: 6,
                pointHoverBackgroundColor: lineColor,
                pointHoverBorderColor: "#ffffff",
                pointHoverBorderWidth: 2,
                pointHitRadius: 20,
            },
        ],
    };

    const crosshairPlugin = {
        id: 'crosshair',
        afterDraw: chart => {
            if (chart.tooltip?._active?.length) {
                const activePoint = chart.tooltip._active[0];
                const ctx = chart.ctx;
                const x = activePoint.element.x;
                const topY = chart.scales.y.top;
                const bottomY = chart.scales.y.bottom;
                
                ctx.save();
                ctx.beginPath();
                ctx.moveTo(x, topY);
                ctx.lineTo(x, bottomY);
                ctx.lineWidth = 1;
                ctx.strokeStyle = 'rgba(148, 163, 184, 0.4)';
                ctx.setLineDash([5, 5]);
                ctx.stroke();
                ctx.restore();
            }
        }
    };

    const options = {
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                enabled: true,
                mode: 'index',
                intersect: false,
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                titleColor: '#64748b',
                bodyColor: '#0f172a',
                borderColor: 'rgba(226, 232, 240, 0.8)',
                borderWidth: 1,
                padding: { top: 12, bottom: 12, left: 16, right: 16 },
                displayColors: false,
                cornerRadius: 12,
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                titleFont: {
                    family: 'Gabarito',
                    size: 13
                },
                bodyFont: {
                    family: 'Gabarito',
                    size: 15,
                    weight: 'bold'
                },
                callbacks: {
                    label: function(context) {
                        const val = context.parsed.y;
                        return val ? Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : val;
                    }
                }
            },
        },
        scales: {
            x: {
                grid: {
                    display: false,
                    drawBorder: false,
                },
                border: { display: false },
                ticks: {
                    color: "#94a3b8",
                    font: { family: "Gabarito" },
                    align: 'inner',
                    callback: function (val, index) {
                        return index % skipInterval === 0 ? this.getLabelForValue(val) : '';
                    },
                    maxRotation: 0,
                    autoSkip: false,
                },
            },
            y: {
                position: 'right',
                grid: {
                    display: true,
                    color: "rgba(0, 0, 0, 0.03)",
                    drawBorder: false,
                },
                border: { display: false },
                ticks: {
                    color: "#94a3b8",
                    font: { family: "Gabarito" },
                    padding: 10,
                    callback: function(val) {
                        return Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                    }
                }
            }
        },
        interaction: {
            mode: 'index',
            intersect: false,
        },
        animation: {
            duration: 1200,
            easing: 'easeOutQuart'
        },
    };

    return (
        <div ref={containerRef} className={className}>
            <Line ref={chartRef} data={chartData} options={options} plugins={[crosshairPlugin]}/>
        </div>
    );
};

export default LineChart;